import { API } from '@/config/environment';

const CMS_BASE_URL = API.CMS_API_URL;
const MEDIA_CDN = API.MEDIA_PUBLIC_CDN;

/**
 * Prepend the media CDN hostname to CMS-relative media paths.
 * Absolute URLs are returned as-is.
 * SonicJS stores uploaded files with a leading /files/ prefix; strip it
 * before prepending the CDN so paths resolve correctly on R2.
 */
export function getMediaUrl(path: string): string {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  const normalized = path.replace(/^\/files\//, '/');
  return `${MEDIA_CDN}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
}

export interface LegalDocument {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  effective_date: string;
  last_modified: string;
  status: 'draft' | 'published' | 'archived';
  display_priority: number;
}

interface CMSContentItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    content?: string;
    excerpt?: string;
    effective_date?: string;
    last_modified?: string;
    display_priority?: number;
    wp_original_id?: number;
  };
  created_at: number;
  updated_at: number;
}

interface CMSResponse {
  data: CMSContentItem[];
  meta: {
    count: number;
    timestamp: string;
  };
}

function mapContentItem(item: CMSContentItem): LegalDocument {
  return {
    id: item.id,
    title: item.title,
    slug: item.slug,
    status: (item.status as LegalDocument['status']) || 'published',
    content: item.data.content || '',
    excerpt: item.data.excerpt || '',
    effective_date: item.data.effective_date || '',
    last_modified: item.data.last_modified || '',
    display_priority: item.data.display_priority ?? 0,
  };
}

export async function fetchLegalDocuments(): Promise<LegalDocument[]> {
  const url = `${CMS_BASE_URL}/collections/legal/content?filter[status][equals]=published&sort=display_priority&limit=100`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSResponse = await response.json();
  return json.data
    .map(mapContentItem)
    .sort((a, b) => a.display_priority - b.display_priority);
}

export async function fetchLegalDocumentBySlug(slug: string): Promise<LegalDocument | null> {
  // Server-side filtering (api-filter middleware): fetch just the one matching doc
  const url = `${CMS_BASE_URL}/collections/legal/content?filter[slug][equals]=${encodeURIComponent(slug)}&filter[status][equals]=published&limit=1`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSResponse = await response.json();
  const item = json.data[0];
  return item && item.status === 'published' ? mapContentItem(item) : null;
}

// ── Blog Posts ──

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featured_image: string;
  author: string;
  publish_date: string;
  status: string;
  categories: string[];
  tags: string[];
  wp_original_url: string;
}

interface CMSBlogPostItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    content?: string;
    excerpt?: string;
    featured_image?: string;
    author?: string;
    publish_date?: string;
    categories?: string[];
    tags?: string[];
    wp_original_id?: number;
    wp_original_url?: string;
  };
  created_at: number;
  updated_at: number;
}

interface CMSBlogResponse {
  data: CMSBlogPostItem[];
  meta: {
    count: number;
    total?: number; // accurate total match count from api-filter middleware
    timestamp?: string;
  };
}

/**
 * Rewrite relative /blog/posts/ paths in HTML content to use the CDN hostname.
 */
function rewriteMediaPaths(html: string): string {
  if (!html) return '';
  return html.replace(/(?:src|poster)="(\/blog\/posts\/[^"]+)"/g, (_match, p) => {
    return _match.replace(p, getMediaUrl(p));
  });
}

/**
 * Strip leading empty <figure> tags that produce blank space above the article.
 */
function stripLeadingEmptyFigures(html: string): string {
  return html.replace(/^(\s*<figure[^>]*>\s*<img[^>]*src="data:image\/svg\+xml;[^"]*"[^>]*\/?\s*>\s*<\/figure>\s*)+/i, '');
}

function mapBlogPost(item: CMSBlogPostItem): BlogPost {
  return {
    id: item.id,
    title: item.title,
    slug: item.slug,
    status: item.status || 'published',
    content: stripLeadingEmptyFigures(rewriteMediaPaths(item.data.content || '')),
    excerpt: item.data.excerpt || '',
    featured_image: getMediaUrl(item.data.featured_image || ''),
    author: item.data.author || '',
    publish_date: item.data.publish_date || '',
    categories: item.data.categories || [],
    tags: item.data.tags || [],
    wp_original_url: item.data.wp_original_url || '',
  };
}

export async function fetchBlogPosts(limit = 50, offset = 0): Promise<{ posts: BlogPost[]; total: number }> {
  const url = `${CMS_BASE_URL}/collections/blog_posts/content?filter[status][equals]=published&limit=${limit}&offset=${offset}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSBlogResponse = await response.json();
  const posts = json.data
    .map(mapBlogPost)
    .sort((a, b) => new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime());

  return { posts, total: json.meta.total ?? json.meta.count };
}

// ── Gallery Items ──

export interface GalleryItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  excerpt: string;
  demo_url: string;
  ad_format: string;
  preview_ad_slot: string;
  adventive_ad_tag: string;
  display_platform: string[];
  industry: string[];
  included_components: string[];
  featured_image: string;
  wp_original_url: string;
  console_import_url: string;
  allow_console_import: boolean;
  companion_ad_slots: string;
  published_to_gallery: boolean;
}

interface CMSGalleryItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    description?: string;
    excerpt?: string;
    demo_url?: string;
    ad_format?: string;
    preview_ad_slot?: string;
    display_platform?: string | string[];
    industry?: string | string[];
    included_components?: string | string[];
    featured_image?: string;
    adventive_ad_tag?: string;
    companion_ad_slots?: string;
    console_import_url?: string;
    allow_console_import?: boolean;
    published_to_gallery?: boolean;
    wp_original_id?: number;
    wp_original_url?: string;
  };
  created_at: number;
  updated_at: number;
}

interface CMSGalleryResponse {
  data: CMSGalleryItem[];
  meta: {
    count: number;
    total?: number; // accurate total match count from api-filter middleware
    timestamp?: string;
  };
}

function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.split(',').map(s => s.trim()).filter(Boolean);
}

function mapGalleryItem(item: CMSGalleryItem): GalleryItem {
  const featuredImage = item.data.featured_image || '';
  return {
    id: item.id,
    title: item.title,
    slug: item.slug,
    description: item.data.description || '',
    excerpt: item.data.excerpt || item.data.description || '',
    demo_url: item.data.demo_url || '',
    ad_format: item.data.ad_format || '',
    preview_ad_slot: item.data.preview_ad_slot || '',
    adventive_ad_tag: item.data.adventive_ad_tag || '',
    display_platform: toArray(item.data.display_platform),
    industry: toArray(item.data.industry),
    included_components: toArray(item.data.included_components),
    featured_image: /^https?:\/\//.test(featuredImage) ? featuredImage : getMediaUrl(featuredImage),
    wp_original_url: item.data.wp_original_url || '',
    console_import_url: item.data.console_import_url || '',
    allow_console_import: item.data.allow_console_import === true,
    companion_ad_slots: item.data.companion_ad_slots || '',
    published_to_gallery: item.data.published_to_gallery !== false,
  };
}

export async function fetchGalleryItemBySlug(slug: string): Promise<GalleryItem | null> {
  // Server-side filtering: fetch just the one matching gallery item
  const url = `${CMS_BASE_URL}/collections/gallery/content?filter[slug][equals]=${encodeURIComponent(slug)}&filter[status][equals]=published&limit=1`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);
  const json: CMSGalleryResponse = await response.json();
  const item = json.data[0];
  return item && item.status === 'published' ? mapGalleryItem(item) : null;
}

export async function fetchGalleryItems(limit = 300): Promise<GalleryItem[]> {
  const url = `${CMS_BASE_URL}/collections/gallery/content?filter[status][equals]=published&limit=${limit}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSGalleryResponse = await response.json();
  return json.data.map(mapGalleryItem).filter(item => item.published_to_gallery);
}

// ── Gallery: server-side filtered fetch (api-filter middleware) ──
//
// Pushes the dropdown filters into the CMS query instead of fetching the whole
// catalog and filtering in the browser.
//
// Notes / limitations of the current api-filter middleware:
//  - `ad_format` is a scalar string  → exact match via filter[ad_format][equals].
//  - `industry` / `included_components` are JSON ARRAYS. The middleware has no
//    array-contains operator, but json_extract returns the array's JSON text,
//    so filter[industry][contains]=Fashion matches the serialized array as a
//    substring. Good enough for distinct option labels; not a precise set match.
//  - `search` is multi-field in the UI (title/excerpt/format/industry). The
//    middleware can only `contains` one field at a time, so server search here
//    is title-only. Full multi-field search still needs the client path.

export interface GalleryQuery {
  format?: string;     // ad_format (exact)
  industry?: string;   // matched within the JSON array (contains)
  component?: string;  // included_components (contains)
  search?: string;     // title contains (single-field, best-effort)
  limit?: number;
  offset?: number;
}

export async function fetchGalleryItemsFiltered(
  query: GalleryQuery = {}
): Promise<{ items: GalleryItem[]; total: number }> {
  const params = new URLSearchParams();
  params.set('filter[status][equals]', 'published');
  if (query.format && query.format !== 'all') params.set('filter[ad_format][equals]', query.format);
  if (query.industry && query.industry !== 'all') params.set('filter[industry][contains]', query.industry);
  if (query.component && query.component !== 'all') params.set('filter[included_components][contains]', query.component);
  if (query.search) params.set('filter[title][contains]', query.search);
  params.set('limit', String(query.limit ?? 60));
  params.set('offset', String(query.offset ?? 0));

  const url = `${CMS_BASE_URL}/collections/gallery/content?${params.toString()}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json: CMSGalleryResponse = await response.json();
  const items = json.data.map(mapGalleryItem).filter(item => item.published_to_gallery);
  return { items, total: json.meta.total ?? items.length };
}

/** Featured gallery items via server-side filter[featured][equals]=true. */
export async function fetchFeaturedGalleryItems(limit = 12): Promise<GalleryItem[]> {
  const url = `${CMS_BASE_URL}/collections/gallery/content?filter[featured][equals]=true&filter[status][equals]=published&limit=${limit}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);
  const json: CMSGalleryResponse = await response.json();
  return json.data.map(mapGalleryItem).filter(item => item.published_to_gallery);
}

// ── Career Listings ──

export interface CareerListing {
  id: string;
  title: string;
  slug: string;
  department: string;
  location: string;
  employment_type: string;
  description: string;
  status: string;
}

interface CMSCareerItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    department?: string;
    location?: string;
    employment_type?: string;
    description?: string;
  };
  created_at: number;
  updated_at: number;
}

interface CMSCareerResponse {
  data: CMSCareerItem[];
  meta: {
    count: number;
    timestamp: string;
  };
}

function mapCareerItem(item: CMSCareerItem): CareerListing {
  return {
    id: item.id,
    title: item.title,
    slug: item.slug,
    status: item.status || 'published',
    department: item.data.department || '',
    location: item.data.location || '',
    employment_type: item.data.employment_type || 'Full-time',
    description: item.data.description || '',
  };
}

export async function fetchCareerListings(): Promise<CareerListing[]> {
  const url = `${CMS_BASE_URL}/collections/careers/content?filter[status][equals]=published&limit=100`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSCareerResponse = await response.json();
  return json.data.map(mapCareerItem);
}

export async function fetchBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  // Server-side filtering: fetch just the one matching post
  const url = `${CMS_BASE_URL}/collections/blog_posts/content?filter[slug][equals]=${encodeURIComponent(slug)}&filter[status][equals]=published&limit=1`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
  }

  const json: CMSBlogResponse = await response.json();
  const item = json.data[0];
  return item && item.status === 'published' ? mapBlogPost(item) : null;
}

// ── Home Sections ──

export interface HomeSectionData {
  section_id: string;
  content: Record<string, unknown>;
}

interface CMSHomeSectionItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    section_id?: string;
    content_json?: string;
  };
  created_at: number;
  updated_at: number;
}

interface CMSHomeSectionResponse {
  data: CMSHomeSectionItem[];
  meta: { count: number; timestamp: string };
}

function parseJsonField<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export async function fetchHomeSections(): Promise<Record<string, Record<string, unknown>>> {
  const url = `${CMS_BASE_URL}/collections/home_sections/content?filter[status][equals]=published&limit=50`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json: CMSHomeSectionResponse = await response.json();
  const result: Record<string, Record<string, unknown>> = {};

  for (const item of json.data) {
    const sectionId = item.data.section_id;
    if (!sectionId) continue;
    result[sectionId] = parseJsonField<Record<string, unknown>>(item.data.content_json, {});
  }

  return result;
}

// ── Solution Pages ──

export interface SolutionPageData {
  slug: string;
  industry: string;
  heroHeadline: string;
  heroSubheadline: string;
  heroImage: string;
  heroAd?: string;
  section2Title: string;
  section2Content: string;
  section2Ad?: string;
  section3Title: string;
  section3Content: string;
  section3Ad?: string;
  section4Title: string;
  section4Content: string;
  accentColor: string;
  stats: { value: string; label: string }[];
  benefits: string[];
}

interface CMSSolutionItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    industry?: string;
    hero_headline?: string;
    hero_subheadline?: string;
    hero_image?: string;
    hero_ad_url?: string;
    section2_title?: string;
    section2_content?: string;
    section2_ad_url?: string;
    section3_title?: string;
    section3_content?: string;
    section3_ad_url?: string;
    section4_title?: string;
    section4_content?: string;
    accent_color?: string;
    stats_json?: string;
    benefits_json?: string;
    sort_order?: number;
  };
  created_at: number;
  updated_at: number;
}

interface CMSSolutionResponse {
  data: CMSSolutionItem[];
  meta: { count: number; timestamp: string };
}

function mapSolutionPage(item: CMSSolutionItem): SolutionPageData {
  return {
    slug: item.slug,
    industry: item.data.industry || '',
    heroHeadline: item.data.hero_headline || '',
    heroSubheadline: item.data.hero_subheadline || '',
    heroImage: item.data.hero_image || '',
    heroAd: item.data.hero_ad_url || undefined,
    section2Title: item.data.section2_title || '',
    section2Content: item.data.section2_content || '',
    section2Ad: item.data.section2_ad_url || undefined,
    section3Title: item.data.section3_title || '',
    section3Content: item.data.section3_content || '',
    section3Ad: item.data.section3_ad_url || undefined,
    section4Title: item.data.section4_title || '',
    section4Content: item.data.section4_content || '',
    accentColor: item.data.accent_color || 'bg-blue-100 text-blue-700',
    stats: parseJsonField<{ value: string; label: string }[]>(item.data.stats_json, []),
    benefits: parseJsonField<string[]>(item.data.benefits_json, []),
  };
}

export async function fetchSolutionPages(): Promise<SolutionPageData[]> {
  const url = `${CMS_BASE_URL}/collections/solution_pages/content?filter[status][equals]=published&limit=50`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json: CMSSolutionResponse = await response.json();
  return json.data.map(mapSolutionPage);
}

export async function fetchSolutionPageBySlug(slug: string): Promise<SolutionPageData | null> {
  // Server-side filtering: fetch just the one matching solution page
  const url = `${CMS_BASE_URL}/collections/solution_pages/content?filter[slug][equals]=${encodeURIComponent(slug)}&filter[status][equals]=published&limit=1`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json: CMSSolutionResponse = await response.json();
  const item = json.data[0];
  return item && item.status === 'published' ? mapSolutionPage(item) : null;
}

// ── Platform Pages ──

export interface PlatformPageData {
  pageId: string;
  badgeLabel: string;
  heroHeadline: string;
  heroHeadlineGradient: string;
  heroSubheadline: string;
  sections: Record<string, unknown>[];
  stats: { value: string; label: string }[];
  ctaHeadline: string;
  ctaSubheadline: string;
  images: Record<string, string>;
}

interface CMSPlatformItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  collectionId: string;
  data: {
    page_id?: string;
    badge_label?: string;
    hero_headline?: string;
    hero_headline_gradient?: string;
    hero_subheadline?: string;
    sections_json?: string;
    stats_json?: string;
    cta_headline?: string;
    cta_subheadline?: string;
    images_json?: string;
  };
  created_at: number;
  updated_at: number;
}

interface CMSPlatformResponse {
  data: CMSPlatformItem[];
  meta: { count: number; timestamp: string };
}

function mapPlatformPage(item: CMSPlatformItem): PlatformPageData {
  return {
    pageId: item.data.page_id || '',
    badgeLabel: item.data.badge_label || '',
    heroHeadline: item.data.hero_headline || '',
    heroHeadlineGradient: item.data.hero_headline_gradient || '',
    heroSubheadline: item.data.hero_subheadline || '',
    sections: parseJsonField<Record<string, unknown>[]>(item.data.sections_json, []),
    stats: parseJsonField<{ value: string; label: string }[]>(item.data.stats_json, []),
    ctaHeadline: item.data.cta_headline || '',
    ctaSubheadline: item.data.cta_subheadline || '',
    images: parseJsonField<Record<string, string>>(item.data.images_json, {}),
  };
}

export async function fetchPlatformPage(pageId: string): Promise<PlatformPageData | null> {
  // Server-side filtering on the page_id data field: fetch just the one match
  const url = `${CMS_BASE_URL}/collections/platform_pages/content?filter[page_id][equals]=${encodeURIComponent(pageId)}&filter[status][equals]=published&limit=1`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json: CMSPlatformResponse = await response.json();
  const item = json.data[0];
  return item && item.data.page_id === pageId && item.status === 'published' ? mapPlatformPage(item) : null;
}

// ─── Brand / Brandkit ──────────────────────────────────────────────────────────

export interface BrandLogoVariant {
  id: string;
  type: 'wordmark' | 'logomark' | 'lettermark';
  variant: 'black' | 'white' | 'color';
  background: 'light' | 'dark' | 'any';
  shape?: 'square';
  formats: { svg?: string; png?: string; jpg?: string; eps?: string };
  usage: string;
  sort_order: number;
}

export interface BrandColor {
  name: string;
  hex: string;
  hsl?: string;
  usage: string;
}

export interface BrandData {
  brand_id: string;
  company_name: string;
  legal_name: string;
  founding_year: number;
  tagline: string;
  tagline_alt: string;
  one_liner: string;
  elevator_pitch: string;
  core_message: string;
  // Complex fields stored as JSON strings in CMS, parsed on fetch
  logos: BrandLogoVariant[];
  colors: Record<string, BrandColor | { name: string; from: string; to: string; usage: string; tailwind: string }[]>;
  typography: {
    font_primary: { family: string; weights: number[]; google_url: string; usage: string };
    font_mono: { family: string; google_url: string; usage: string };
    font_stack: string;
    type_scale: { name: string; size: string; tailwind_class: string; weight: string; usage: string }[];
  };
  social_links: {
    id: string;
    platform: string;
    name: string;
    url: string;
    handle: string;
    enabled: boolean;
    sort_order: number;
  }[];
  messaging_pillars: { pillar: string; message: string; proof: string }[];
  voice_and_tone: { do: string[]; dont: string[] };
  seo_schema_org: Record<string, unknown>;
  // Flat string fields
  seo_site_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_canonical_url: string;
  seo_og_title: string;
  seo_og_description: string;
  seo_og_image: string;
  seo_og_site_name: string;
  seo_twitter_handle: string;
  seo_twitter_title: string;
  seo_twitter_description: string;
  seo_twitter_image: string;
  contact_email_general: string;
  contact_email_support: string;
  contact_email_sales: string;
  contact_email_press: string;
  contact_phone_sales: string;
  url_marketing: string;
  url_app: string;
  url_help: string;
  url_gallery: string;
  url_blog: string;
  url_brand_guide: string;
  icon_favicon_ico: string;
  icon_favicon_32: string;
  icon_favicon_16: string;
  icon_apple_touch: string;
  icon_og_image: string;
  icon_twitter_card: string;
}

// Fields stored as JSON strings in the CMS textarea — parse them on fetch
const JSON_FIELDS = ['logos', 'colors', 'typography', 'social_links', 'messaging_pillars', 'voice_and_tone', 'seo_schema_org'] as const;

function parseBrandJsonFields(raw: Record<string, unknown>): BrandData {
  const result = { ...raw } as Record<string, unknown>;
  for (const field of JSON_FIELDS) {
    if (typeof result[field] === 'string') {
      try { result[field] = JSON.parse(result[field] as string); } catch { /* keep as-is */ }
    }
  }
  return result as unknown as BrandData;
}

let brandCache: BrandData | null = null;

export async function fetchBrand(): Promise<BrandData | null> {
  if (brandCache) return brandCache;

  const url = `${CMS_BASE_URL}/content?collection=brand&limit=1`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);

  const json = await response.json() as { data: { data: Record<string, unknown> }[] };
  const item = json.data?.[0];
  if (!item) return null;

  brandCache = parseBrandJsonFields(item.data);
  return brandCache;
}

// ── Publishers ──

export interface Publisher {
  id: string;
  name: string;
  slug: string;
  logo: string;
  website: string;
  active: boolean;
}

interface CMSPublisherItem {
  id: string;
  title: string;
  slug: string;
  status: string;
  data: {
    name?: string;
    logo?: string;
    website?: string;
    active?: boolean;
  };
}

interface CMSPublisherResponse {
  data: CMSPublisherItem[];
  meta: { count: number; timestamp: string };
}

function mapPublisher(item: CMSPublisherItem): Publisher {
  return {
    id: item.id,
    name: item.data.name || item.title,
    slug: item.slug,
    logo: getMediaUrl(item.data.logo || ''),
    website: item.data.website || '',
    active: item.data.active !== false,
  };
}

export async function fetchPublishers(): Promise<Publisher[]> {
  const url = `${CMS_BASE_URL}/collections/publishers/content?filter[status][equals]=published&limit=50`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`CMS API error: ${response.status}`);
  const json: CMSPublisherResponse = await response.json();
  return json.data.filter(i => i.data.active !== false).map(mapPublisher);
}

/** Convenience: get the primary wordmark URL for a given variant */
export function getBrandLogo(
  logos: BrandLogoVariant[],
  type: 'wordmark' | 'logomark' | 'lettermark',
  variant: 'black' | 'white',
  format: 'svg' | 'png' | 'jpg' = 'svg'
): string {
  const match = logos.find(l => l.type === type && l.variant === variant && !l.shape);
  const raw = match?.formats[format] ?? '';
  return raw ? getMediaUrl(raw) : '';
}
